import {
  CoverageSchema,
  EnrichmentMetadata,
  EnrichmentSource,
} from '@/services/types/coverage-types';

/**
 * Check if a coverage record is from cached data
 */
export function isCachedSuggestion(coverage: CoverageSchema): boolean {
  return (
    coverage.enrichment_source === EnrichmentSource.CACHED &&
    coverage.cache_used === true
  );
}

/**
 * Get display information about the cache source
 */
export function getCacheDisplayInfo(coverage: CoverageSchema): string | null {
  if (!isCachedSuggestion(coverage)) {
    return null;
  }

  const metadata = coverage.enrichment_metadata;
  if (!metadata) {
    return null;
  }

  const sourceMatter = metadata.source_matter_name || 'another matter';
  const ageText = getCacheAgeText(metadata.cache_age_days);

  return `Carrier info from ${sourceMatter}${ageText ? ` (${ageText})` : ''}`;
}

/**
 * Convert cache age in days to human-readable text
 */
export function getCacheAgeText(ageDays?: number): string {
  if (!ageDays || ageDays <= 0) {
    return '';
  }

  if (ageDays === 1) {
    return '1 day ago';
  }

  if (ageDays < 7) {
    return `${ageDays} days ago`;
  }

  if (ageDays < 30) {
    const weeks = Math.round(ageDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }

  const months = Math.round(ageDays / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

/**
 * Get badge properties based on enrichment source
 */
export function getEnrichmentBadgeProps(
  enrichmentSource?: EnrichmentSource | null,
): {
  variant: 'success' | 'secondary' | 'outline';
  text: string;
  className?: string;
} {
  switch (enrichmentSource) {
    case EnrichmentSource.CACHED:
      return {
        variant: 'success',
        text: 'Source: Previously Verified',
        className: 'bg-green-100 text-green-800 border-green-300',
      };
    case EnrichmentSource.PERPLEXITY:
      return {
        variant: 'secondary',
        text: 'Source: AI Enhanced',
        className: 'bg-blue-100 text-blue-800 border-blue-300',
      };
    case EnrichmentSource.EXTRACTION:
      return {
        variant: 'secondary',
        text: 'Source: AI Extraction',
        className: 'bg-blue-100 text-blue-800 border-blue-300',
      };
    case EnrichmentSource.MANUAL:
      return {
        variant: 'outline',
        text: 'Source: Manual Entry',
        className: 'bg-gray-100 text-gray-800 border-gray-300',
      };
    case EnrichmentSource.INTAKE:
      return {
        variant: 'outline',
        text: 'Source: Intake',
        className: 'bg-purple-100 text-purple-800 border-purple-300',
      };
    default:
      return {
        variant: 'outline',
        text: 'Source: Unknown',
        className: 'bg-gray-100 text-gray-800 border-gray-300',
      };
  }
}

/**
 * Get the priority score for sorting cached coverage
 * Higher scores should appear first in lists
 */
export function getCachePriority(coverage: CoverageSchema): number {
  if (isCachedSuggestion(coverage)) {
    // Cached suggestions get highest priority
    return 100;
  }

  if (coverage.enrichment_source === EnrichmentSource.PERPLEXITY) {
    // AI enhanced gets medium priority
    return 50;
  }

  // Manual and standard entries get normal priority
  return 0;
}

/**
 * Get detailed enrichment information for display
 */
export function getEnrichmentDetails(coverage: CoverageSchema): {
  source: string;
  description: string;
  showMetadata: boolean;
  metadata?: EnrichmentMetadata | null;
} {
  const metadata = coverage.enrichment_metadata;

  switch (coverage.enrichment_source) {
    case EnrichmentSource.CACHED:
      return {
        source: 'Previously Approved Coverage',
        description: metadata?.source_matter_name
          ? `Carrier information verified from ${metadata.source_matter_name}`
          : 'Carrier information from previously approved coverage',
        showMetadata: true,
        metadata,
      };

    case EnrichmentSource.PERPLEXITY:
      return {
        source: 'AI Enhanced Search',
        description: 'Carrier information found through automated web search',
        showMetadata: false,
        metadata,
      };

    case EnrichmentSource.EXTRACTION:
      return {
        source: 'AI Extraction',
        description: 'Coverage created from AI extraction and consolidation',
        showMetadata: false,
        metadata,
      };

    case EnrichmentSource.MANUAL:
      return {
        source: 'Manual Entry',
        description: 'Carrier information entered manually',
        showMetadata: false,
        metadata,
      };
    case EnrichmentSource.INTAKE:
      return {
        source: 'Intake Form',
        description: 'Coverage details provided during client intake',
        showMetadata: false,
        metadata,
      };

    default:
      return {
        source: 'Standard Entry',
        description: 'Coverage information from document extraction',
        showMetadata: false,
        metadata,
      };
  }
}

/**
 * Format the match reason for display
 */
export function formatMatchReason(reason?: string): string {
  if (!reason) {
    return 'Exact carrier name match with approved data';
  }

  // Clean up technical match reasons for user display
  return reason
    .replace(/All requirements met - /, '')
    .replace(/exact match with recent approved data/i, 'exact carrier match')
    .replace(/normalized/i, 'standardized');
}
